import React from 'react';
import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';
import styles from './Stat.module.css';

export type StatTone = 'default' | 'muted' | 'success' | 'warning' | 'error' | 'info' | 'accent';
export type StatSize = 'md' | 'lg' | 'xl';

interface StatProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Optional sub-line below the value (e.g. "last 28 days"). */
  sub?: React.ReactNode;
  /** Optional inline trend/delta shown next to the value (e.g. "+12%"). */
  trend?: React.ReactNode;
  /** Optional leading icon rendered next to the value. */
  icon?: LucideIcon;
  /** Semantic color for the value. */
  tone?: StatTone;
  /** Value size: md = text-xl (default), lg = text-2xl, xl = text-3xl. */
  size?: StatSize;
  /** Wrap the stat in a bordered surface card. */
  card?: boolean;
  className?: string;
}

const TONE_CLASS: Record<StatTone, string | undefined> = {
  default: undefined,
  muted: styles.toneMuted,
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  error: styles.toneError,
  info: styles.toneInfo,
  accent: styles.toneAccent,
};

const SIZE_CLASS: Record<StatSize, string | undefined> = {
  md: undefined,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
};

export function Stat({
  label,
  value,
  sub,
  trend,
  icon: Icon,
  tone = 'default',
  size = 'md',
  card = false,
  className,
}: StatProps) {
  return (
    <div className={clsx(styles.root, card && styles.card, className)}>
      <div className={styles.label}>{label}</div>
      <div className={styles.valueRow}>
        <span
          className={clsx(
            styles.value,
            SIZE_CLASS[size],
            TONE_CLASS[tone],
            Icon && styles.withIcon,
          )}
        >
          {Icon && <Icon size={18} strokeWidth={1.75} aria-hidden />}
          {value}
        </span>
        {trend && <span className={styles.trend}>{trend}</span>}
      </div>
      {sub && <div className={styles.sub}>{sub}</div>}
    </div>
  );
}
